/**
 * Argon professional product lightbox zoom.
 * Near-fullscreen PhotoSwipe: delayed dark/blur overlay, zoom level control,
 * white close, centered thumbs.
 */
(function () {
  const PAD_TOP_VH = 0.02;
  const PAD_SIDE_VW = 0.02;
  const THUMB_RESERVE = 72;

  function getOpenPswpElement() {
    return document.querySelector('.pswp.pswp--open');
  }

  function getGallery() {
    return (
      document.querySelector('product-gallery[data-argon-zoom="pro"].argon-zoom--opening') ||
      document.querySelector('product-gallery[data-argon-zoom="pro"]')
    );
  }

  function getPswpInstance(gallery, pswpEl) {
    if (pswpEl?.pswp) return pswpEl.pswp;
    try {
      const lightbox = gallery?.lightBox;
      if (lightbox?.pswp) {
        if (pswpEl) pswpEl.pswp = lightbox.pswp;
        return lightbox.pswp;
      }
    } catch (e) {
      /* ignore */
    }
    return null;
  }

  function readNumber(gallery, attr, fallback) {
    const value = parseFloat(gallery?.getAttribute(attr));
    return Number.isFinite(value) ? value : fallback;
  }

  function zoomLevel(gallery) {
    return Math.max(1, readNumber(gallery, 'data-zoom-level', 1.5));
  }

  function blurPercent(gallery) {
    return Math.min(100, Math.max(0, readNumber(gallery, 'data-zoom-blur', 55)));
  }

  function darknessPercent(gallery) {
    return Math.min(100, Math.max(0, readNumber(gallery, 'data-zoom-darkness', 82)));
  }

  function durationSeconds(gallery) {
    return Math.min(5, Math.max(0, readNumber(gallery, 'data-zoom-duration', 0.5)));
  }

  function closeDurationSeconds(gallery) {
    return Math.min(5, Math.max(0, readNumber(gallery, 'data-zoom-close-duration', 0.5)));
  }

  function thumbBorderColor(gallery) {
    return gallery?.getAttribute('data-zoom-thumb-border') || '#3a3a3a';
  }

  function paddingFn(viewportSize) {
    return {
      top: Math.round(viewportSize.y * PAD_TOP_VH),
      bottom: Math.round(viewportSize.y * 0.012 + THUMB_RESERVE),
      left: Math.round(viewportSize.x * PAD_SIDE_VW),
      right: Math.round(viewportSize.x * PAD_SIDE_VW)
    };
  }

  function applyAtmosphereVars(gallery) {
    const blurPct = blurPercent(gallery);
    const darkPct = darknessPercent(gallery);
    const durationIn = durationSeconds(gallery);
    const durationOut = closeDurationSeconds(gallery);
    const overlayBlurPx = (blurPct / 100) * 40;
    const pageBlurPx = (blurPct / 100) * 22;
    const darkness = Math.min(0.94, (darkPct / 100) * 0.94);
    const durationInCss = `${durationIn.toFixed(1)}s`;
    const durationOutCss = `${durationOut.toFixed(1)}s`;
    const root = document.documentElement;

    root.style.setProperty('--argon-pswp-blur', `${overlayBlurPx.toFixed(1)}px`);
    root.style.setProperty('--argon-pswp-darkness', darkness.toFixed(3));
    root.style.setProperty('--argon-pswp-page-blur', `${pageBlurPx.toFixed(1)}px`);
    root.style.setProperty('--argon-pswp-atmosphere-duration-in', durationInCss);
    root.style.setProperty('--argon-pswp-atmosphere-duration-out', durationOutCss);
  }

  function getAtmosphere() {
    let el = document.querySelector('.argon-pswp-atmosphere');
    if (!el) {
      el = document.createElement('div');
      el.className = 'argon-pswp-atmosphere';
      el.setAttribute('aria-hidden', 'true');
      document.body.appendChild(el);
    }
    return el;
  }

  function setAtmosphereVisible(on, gallery) {
    const el = getAtmosphere();
    const g = gallery || getGallery();
    applyAtmosphereVars(g);
    const durationMs = Math.round((on ? durationSeconds(g) : closeDurationSeconds(g)) * 1000);

    if (on) {
      el.classList.remove('is-closing');
      /* force reflow so open transition always runs from opacity 0 */
      void el.offsetWidth;
      el.classList.add('is-visible');
    } else {
      el.classList.add('is-closing');
      el.classList.remove('is-visible');
      window.setTimeout(() => {
        if (!el.classList.contains('is-visible')) {
          el.classList.remove('is-closing');
        }
      }, durationMs + 50);
    }
  }

  function setPageBlur(on, blurPct, gallery) {
    const root = document.documentElement;
    const g = gallery || getGallery();
    const durationMs = Math.round((on ? durationSeconds(g) : closeDurationSeconds(g)) * 1000);
    if (blurPct <= 0) {
      root.classList.remove('argon-pswp-page-blur', 'argon-pswp-page-blur--on', 'argon-pswp-page-blur--closing');
      return;
    }
    root.classList.add('argon-pswp-page-blur');
    if (on) {
      root.classList.remove('argon-pswp-page-blur--closing');
      requestAnimationFrame(() => root.classList.add('argon-pswp-page-blur--on'));
    } else {
      root.classList.add('argon-pswp-page-blur--closing');
      root.classList.remove('argon-pswp-page-blur--on');
      window.setTimeout(() => {
        if (!getOpenPswpElement()) {
          root.classList.remove('argon-pswp-page-blur', 'argon-pswp-page-blur--closing');
        }
      }, durationMs + 50);
    }
  }

  function beginClose(pswpEl, gallery) {
    const g = gallery || getGallery();
    if (pswpEl) {
      pswpEl.classList.add('argon-pswp--closing');
      pswpEl.classList.remove('argon-pswp--ready');
    }
    /* Reverse backdrop with its own close-speed slider */
    setAtmosphereVisible(false, g);
    setPageBlur(false, blurPercent(g), g);
  }

  function markAtmosphereReady(pswpEl, gallery) {
    if (!pswpEl || pswpEl.classList.contains('argon-pswp--closing')) return;
    pswpEl.classList.add('argon-pswp--ready');
    setAtmosphereVisible(true, gallery);
    setPageBlur(true, blurPercent(gallery), gallery);
  }

  function configureZoomOptions(pswp, gallery) {
    if (!pswp?.options) return;
    const level = zoomLevel(gallery);
    pswp.options.paddingFn = paddingFn;
    /* No PhotoSwipe bg dim — prevents the open flash */
    pswp.options.bgOpacity = 0;
    pswp.options.showHideAnimationType = 'zoom';
    pswp.options.clickToCloseNonZoomable = false;
    pswp.options.maxZoomLevel = level;
    pswp.options.secondaryZoomLevel = (zoomLevels) => {
      const target = zoomLevels.fit * level;
      return Math.min(target, zoomLevels.max || target);
    };
    try {
      pswp.options.clickAction = 'zoom';
      pswp.options.dblClickAction = false;
    } catch (e) {
      /* ignore */
    }
  }

  function patchLightbox(gallery) {
    if (!gallery || gallery.dataset.argonLightboxPatched === 'true') return;
    gallery.dataset.argonLightboxPatched = 'true';

    let lightbox;
    try {
      lightbox = gallery.lightBox;
    } catch (e) {
      return;
    }
    if (!lightbox) return;

    lightbox.addFilter('itemData', (itemData) => {
      itemData.thumbCropped = false;
      return itemData;
    });

    lightbox.addFilter('maxZoomLevel', () => zoomLevel(gallery));

    lightbox.on('beforeOpen', () => {
      configureZoomOptions(lightbox.pswp, gallery);
    });

    lightbox.on('openingAnimationStart', () => {
      const el = lightbox.pswp?.element || getOpenPswpElement();
      if (el) enhancePswp(el, gallery);
    });

    lightbox.on('openingAnimationEnd', () => {
      const el = lightbox.pswp?.element || getOpenPswpElement();
      if (el) markAtmosphereReady(el, gallery);
    });

    lightbox.on('close', () => {
      beginClose(lightbox.pswp?.element || getOpenPswpElement(), gallery);
    });

    lightbox.on('destroy', () => {
      gallery.classList.remove('argon-zoom--opening');
    });
  }

  function enhancePswp(pswpEl, galleryHint) {
    if (!pswpEl) return;
    const gallery = galleryHint || getGallery();
    if (!gallery || gallery.getAttribute('data-argon-zoom') !== 'pro') return;

    if (pswpEl.dataset.argonEnhanced !== 'true') {
      pswpEl.dataset.argonEnhanced = 'true';
      pswpEl.classList.add('argon-pswp');
      pswpEl.style.setProperty('--argon-pswp-thumb-border', thumbBorderColor(gallery));
      applyAtmosphereVars(gallery);
      getAtmosphere();

      const instance = getPswpInstance(gallery, pswpEl);
      if (instance) {
        configureZoomOptions(instance, gallery);
        instance.on?.('change', () => syncActiveThumb(pswpEl, instance.currIndex || 0));
        instance.on?.('zoomPanUpdate', () => updateZoomCursorClass(pswpEl, instance));
        instance.on?.('initialZoomPan', () => updateZoomCursorClass(pswpEl, instance));
        instance.on?.('close', () => beginClose(pswpEl, gallery));
      }

      buildCloseButton(pswpEl, gallery);
      buildThumbStrip(pswpEl, gallery);
      bindZoomCursorState(pswpEl, instance);
      syncActiveThumb(pswpEl, instance?.currIndex || 0);
    }

    /* Fallback if openingAnimationEnd was missed */
    requestAnimationFrame(() => {
      window.setTimeout(() => {
        if (
          pswpEl.classList.contains('pswp--open') &&
          !pswpEl.classList.contains('argon-pswp--ready') &&
          !pswpEl.classList.contains('argon-pswp--closing')
        ) {
          markAtmosphereReady(pswpEl, gallery);
        }
      }, 380);
    });
  }

  function buildCloseButton(pswpEl, gallery) {
    if (pswpEl.querySelector('.argon-pswp-close')) return;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'argon-pswp-close';
    btn.setAttribute('aria-label', 'Close');
    btn.innerHTML = `
      <svg width="22" height="22" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1.35" stroke-linecap="round"/>
      </svg>
    `;
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      getPswpInstance(gallery, pswpEl)?.close?.();
    });
    pswpEl.appendChild(btn);
  }

  function buildThumbStrip(pswpEl, gallery) {
    if (pswpEl.querySelector('.argon-pswp-thumbs')) return;

    const images = Array.from(
      gallery.querySelectorAll(
        '.product-gallery__carousel .product-gallery__media[data-media-type="image"]:not([hidden]) > img'
      )
    );
    if (images.length === 0) return;

    const wrap = document.createElement('div');
    wrap.className = 'argon-pswp-thumbs';
    wrap.innerHTML = `
      <div class="argon-pswp-thumbs__frame">
        <div class="argon-pswp-thumbs__track" role="tablist"></div>
      </div>
      <div class="argon-pswp-thumbs__nav">
        <button type="button" class="argon-pswp-thumbs__btn argon-pswp-thumbs__btn--prev" aria-label="Previous">
          <svg width="14" height="14" viewBox="0 0 16 18" fill="none" aria-hidden="true"><path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"/></svg>
        </button>
        <button type="button" class="argon-pswp-thumbs__btn argon-pswp-thumbs__btn--next" aria-label="Next">
          <svg width="14" height="14" viewBox="0 0 16 18" fill="none" aria-hidden="true"><path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square"/></svg>
        </button>
      </div>
    `;

    const track = wrap.querySelector('.argon-pswp-thumbs__track');
    images.forEach((img, index) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'argon-pswp-thumbs__item';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-label', `Image ${index + 1}`);
      btn.dataset.index = String(index);
      const thumb = document.createElement('img');
      thumb.src = img.currentSrc || img.src;
      thumb.alt = '';
      thumb.loading = 'lazy';
      btn.appendChild(thumb);
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const pswp = getPswpInstance(gallery, pswpEl);
        if (pswp?.goTo) pswp.goTo(index);
        syncActiveThumb(pswpEl, index);
      });
      track.appendChild(btn);
    });

    wrap.querySelector('.argon-pswp-thumbs__btn--prev').addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      getPswpInstance(gallery, pswpEl)?.prev?.();
    });
    wrap.querySelector('.argon-pswp-thumbs__btn--next').addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      getPswpInstance(gallery, pswpEl)?.next?.();
    });

    wrap.addEventListener('click', (event) => event.stopPropagation());
    wrap.addEventListener('pointerdown', (event) => event.stopPropagation());

    pswpEl.appendChild(wrap);
  }

  function syncActiveThumb(pswpEl, index) {
    const track = pswpEl.querySelector('.argon-pswp-thumbs__track');
    const items = pswpEl.querySelectorAll('.argon-pswp-thumbs__item');
    items.forEach((item, i) => {
      const active = i === index;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-selected', active ? 'true' : 'false');
      if (active && track) {
        const left = item.offsetLeft - track.clientWidth / 2 + item.clientWidth / 2;
        track.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }
    });
  }

  function updateZoomCursorClass(pswpEl, instance) {
    const zoomed =
      pswpEl.classList.contains('pswp--zoomed-in') ||
      (instance?.currSlide &&
        instance.currSlide.currZoomLevel > (instance.currSlide.zoomLevels?.fit || 1) + 0.01);
    pswpEl.classList.toggle('argon-pswp--zoomed', Boolean(zoomed));
  }

  function bindZoomCursorState(pswpEl, instance) {
    updateZoomCursorClass(pswpEl, instance);
    const observer = new MutationObserver(() =>
      updateZoomCursorClass(pswpEl, getPswpInstance(getGallery(), pswpEl))
    );
    observer.observe(pswpEl, { attributes: true, attributeFilter: ['class'] });
  }

  function observeLightboxes() {
    const tryEnhance = () => {
      const open = getOpenPswpElement();
      if (open) enhancePswp(open);
    };

    tryEnhance();

    const mo = new MutationObserver(() => tryEnhance());
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  function prepareGallery(gallery) {
    if (!gallery || gallery.getAttribute('data-argon-zoom') !== 'pro') return;
    gallery.classList.add('argon-zoom--opening');
    patchLightbox(gallery);
  }

  function markOpeningGallery(event) {
    const gallery =
      event.target?.closest?.('product-gallery[data-argon-zoom="pro"]') || event.target;
    if (!gallery || gallery.tagName !== 'PRODUCT-GALLERY') return;
    prepareGallery(gallery);

    let attempts = 0;
    const waitOpen = window.setInterval(() => {
      attempts += 1;
      const pswpEl = getOpenPswpElement();
      if (pswpEl) {
        window.clearInterval(waitOpen);
        enhancePswp(pswpEl, gallery);
      } else if (attempts > 40) {
        window.clearInterval(waitOpen);
      }
    }, 50);
  }

  document.addEventListener('lightbox:open', markOpeningGallery, true);
  document.addEventListener(
    'click',
    (event) => {
      const gallery = event.target?.closest?.('product-gallery[data-argon-zoom="pro"]');
      if (gallery && event.target.tagName === 'IMG') {
        prepareGallery(gallery);
      }
    },
    true
  );

  function boot() {
    document.querySelectorAll('product-gallery[data-argon-zoom="pro"]').forEach(patchLightbox);
    observeLightboxes();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
